/**
 * Timezone Utility for CANmail Schedule Feature
 *
 * Server Location: Calgary, Alberta, Canada
 * Server Timezone: America/Edmonton (Mountain Time)
 * - Winter (MST): UTC-7
 * - Summer (MDT): UTC-6
 *
 * DST Observance: Yes (March - November)
 */

const SERVER_TIMEZONE = 'America/Edmonton';

/**
 * Convert user's local time to Calgary server time
 * Uses Intl.DateTimeFormat for accurate DST handling
 */
export const convertUserTimeToServerTime = (userLocalDate) => {
  if (!(userLocalDate instanceof Date) || isNaN(userLocalDate.getTime())) {
    throw new Error('Invalid date object provided');
  }

  // Convert the user's selected moment to Calgary timezone
  const calgaryFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: SERVER_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  // Extract Calgary time components
  const parts = calgaryFormatter.formatToParts(userLocalDate);
  const calgaryTime = {};
  parts.forEach(part => {
    if (part.type !== 'literal') {
      calgaryTime[part.type] = part.value;
    }
  });

  // Format as ISO string for database storage (useUTC: false ensures no conversion)
  const serverTimeString = `${calgaryTime.year}-${calgaryTime.month}-${calgaryTime.day}T${calgaryTime.hour}:${calgaryTime.minute}:${calgaryTime.second}`;

  return serverTimeString;
};

/**
 * Convert Calgary server time string to user's local time
 */
export const convertServerTimeToUserTime = (serverTimeString) => {
  if (!serverTimeString) {
    throw new Error('Server time string is required');
  }

  // Parse the server time string - handle both "YYYY-MM-DD HH:mm:ss" and "YYYY-MM-DDTHH:mm:ss"
  const cleanedString = serverTimeString.replace(' ', 'T');
  const [datePart, timePart] = cleanedString.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes, seconds = 0] = timePart.split(':').map(Number);

  // Parse server time and find equivalent UTC moment for Calgary timezone
  const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));

  const calgaryFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: SERVER_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const calgaryParts = calgaryFormatter.formatToParts(utcDate);
  const calgaryValues = {};
  calgaryParts.forEach(part => {
    if (part.type !== 'literal') {
      calgaryValues[part.type] = parseInt(part.value, 10);
    }
  });

  const calgaryDisplayTime = Date.UTC(
    calgaryValues.year,
    calgaryValues.month - 1,
    calgaryValues.day,
    calgaryValues.hour,
    calgaryValues.minute,
    calgaryValues.second
  );

  const targetTime = Date.UTC(year, month - 1, day, hours, minutes, seconds);
  const offset = targetTime - calgaryDisplayTime;

  const correctUtcTime = utcDate.getTime() + offset;

  return new Date(correctUtcTime);
};

/**
 * Format a Date object as server time string
 */
export const formatServerTimeString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

/**
 * Get the current Calgary server time offset in minutes
 * Automatically accounts for DST
 */
export const getServerTimezoneOffset = (date = new Date()) => {
  // Use Intl API to determine current offset
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: SERVER_TIMEZONE,
    timeZoneName: 'shortOffset'
  });

  const parts = formatter.formatToParts(date);
  const offsetPart = parts.find(part => part.type === 'timeZoneName');

  if (offsetPart && offsetPart.value) {
    // Parse offset like "GMT-7" or "GMT-6"
    const match = offsetPart.value.match(/GMT([+-])(\d+)/);
    if (match) {
      const sign = match[1] === '+' ? 1 : -1;
      const hours = parseInt(match[2], 10);
      return sign * hours * 60;
    }
  }

  // Fallback: Check if DST is active (March-November)
  const month = date.getMonth();
  const isDST = month >= 2 && month < 10;

  return isDST ? -360 : -420;
};

/**
 * Check if Calgary is currently observing Daylight Saving Time
 */
export const isCalgaryDST = (date = new Date()) => {
  const offset = getServerTimezoneOffset(date);
  return offset === -360;
};

/**
 * Get human-readable timezone info for Calgary
 */
export const getCalgaryTimezoneInfo = (date = new Date()) => {
  const offset = getServerTimezoneOffset(date);
  const isDST = isCalgaryDST(date);
  const hours = Math.abs(offset) / 60;

  return {
    timezone: SERVER_TIMEZONE,
    abbreviation: isDST ? 'MDT' : 'MST',
    offsetMinutes: offset,
    offsetHours: offset / 60,
    offsetString: `UTC${offset > 0 ? '+' : ''}${offset / 60}`,
    isDST,
    name: isDST ? 'Mountain Daylight Time' : 'Mountain Standard Time',
  };
};

/**
 * Validate that the scheduled time is not during a DST transition gap
 */
export const validateScheduledTime = (serverTimeString) => {
  // Parse the time
  const [datePart, timePart] = serverTimeString.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes, seconds] = timePart.split(':').map(Number);

  // Check if scheduling during DST spring-forward gap (non-existent time)
  if (
    year === 2025 &&
    month === 3 && // March (1-indexed)
    day === 9 &&
    hours >= 2 && hours < 3
  ) {
    return {
      valid: false,
      reason: 'Cannot schedule during DST transition (2:00-3:00 AM on March 9, 2025 does not exist in Calgary)'
    };
  }

  return { valid: true };
};
